// janus-engine/spells/spell-prep-commit.ts — Lane L7 (2026-09-26): the browser adapter for the prepared-spell commit.
// Used by the PC-sheet Prepare Spells tab (post-template-late).
//
// The caller has already decided the gameplay with prepared-spells.ts; this sends the envelope to
// spell_prep_truth_commit_inc.php with the CAS inputs (movement_rev + the prepared-set fingerprint) read from the freshest row
// the page holds, adopts the server's truth on a 409 `stale` and re-runs the caller's gate ONCE before resending, and hands
// the saved frame to the websocket outbox and installer (window.ftApplySpellPrepFrame — the same function the
// `janus_spell_prep_truth` frame runs on every other viewer) so this page's caster / encounter rows update at once.
import { normalizePreparedMap, preparedFingerprint, SPELL_PREP_SCHEMA, type PreparedMap, type SpellPrepAction, type Verdict } from './prepared-spells';
import { encounterCreatureField } from '../../shared/array-types';
import { withLogParticipants } from '../../shared/log-participant-snapshot';
import { ensureActorRowHeld, runRowOnly } from '../../shared/actor-row-loader';
import type { SpellListSwitchPlan } from './spell-list-switch';
import { saveAppliedTruth, type TruthCommand } from '../../shared/applied-truth-transport';
import { withAsyncDeadline } from '../../shared/async-deadline';
import { readPlayerCharacterField } from '../display/player-character-field';
import { classIdOfSlot } from './spell-prep-table';

const ENDPOINT = '/options/game/spells/prepare_spells/spell_prep_truth_commit_inc.php';
type Row = Record<string, unknown>;
const asRow = (v: unknown): Row | null => (v !== null && typeof v === 'object' && !Array.isArray(v) ? (v as Row) : null);
const upper = (v: unknown): string => String(v ?? '').trim().toUpperCase();
const int = (v: unknown, fb = 0): number => { const n = Number(Array.isArray(v) ? v[0] : v); return Number.isFinite(n) ? Math.trunc(n) : fb; };

export interface SpellPrepTruth { rev: number; active: number; prepared: PreparedMap; fingerprint: string }
/** Last server truth per caster (answers + frames); newer than any row snapshot the page may still hold. */
const truthByCaster = new Map<string, SpellPrepTruth>();
export function noteSpellPrepTruth(casterId: string, t: { rev: number; active: number; prepared: unknown }): void {
    const prepared = normalizePreparedMap(t.prepared);
    const prior = truthByCaster.get(upper(casterId));
    if (prior && prior.rev > t.rev) return;
    truthByCaster.set(upper(casterId), { rev: t.rev, active: t.active, prepared, fingerprint: preparedFingerprint(prepared, t.active) });
}

// Committed frames reach every bundle as the `ft:spell-prep-truth` window event (websocket/spell-prep-truth.ts re-dispatches
// them): bank each as the caster's newest known truth so this bundle's next CAS carries it.
if (typeof window !== 'undefined') {
    window.addEventListener('ft:spell-prep-truth', (ev) => {
        const d = (ev as CustomEvent<{ actor_id?: string; rev?: number; sections?: Row; adopted?: boolean }>).detail;
        if (!d || d.adopted || !Number.isInteger(d.rev) || !asRow(d.sections)) return;
        noteSpellPrepTruth(String(d.actor_id ?? ''), { rev: Number(d.rev), active: int(d.sections!.spell_list_prepared, 1), prepared: d.sections!.prepared });
    });
}

interface FtWindow { FateTide?: { casterCache?: Map<string, unknown>; encounterCache?: Map<string, unknown> }; ftApplySpellPrepFrame?: (frame: unknown) => void; ftPlanSpellListSwitch?: (casterId: string, list: number, classNumber: number, row: Row, encounterId: string) => Promise<SpellListSwitchPlan> }
const w = (): FtWindow => window as unknown as FtWindow;

/** Every row the page holds for this caster: the caster cache (EC id / PC id) and any cached encounter member. */
function heldRows(casterId: string): Row[] {
    const id = upper(casterId);
    const rows: Row[] = [];
    const cc = w().FateTide?.casterCache;
    for (const [k, v] of cc?.entries?.() ?? []) if (upper(k) === id && asRow(v)) rows.push(v as Row);
    for (const enc of w().FateTide?.encounterCache?.values?.() ?? []) {
        for (const m of (asRow(enc)?.encounter_creatures_arr as unknown[] | undefined) ?? []) {
            const ec = asRow(asRow(m)?.encounter_creatures);
            if (ec && upper(encounterCreatureField(ec, 'encounter_creature_id')) === id) rows.push(m as Row);
        }
    }
    return rows;
}

/** CAS inputs: the newest of the recorded truth and every held row (the highest movement_rev wins). */
export function currentSpellPrepTruth(casterId: string, fallback?: Row | null): SpellPrepTruth {
    let best: SpellPrepTruth | null = truthByCaster.get(upper(casterId)) ?? null;
    for (const row of [...heldRows(casterId), ...(fallback ? [fallback] : [])]) {
        const ec = asRow(row.encounter_creatures) ?? {};
        const rev = int(encounterCreatureField(ec, 'movement_rev'), 0);
        if (best && best.rev >= rev) continue;
        const active = int(encounterCreatureField(ec, 'spell_list_prepared'), 1) || 1;
        const prepared = normalizePreparedMap(row.encounter_creatures_spells_prepared);
        best = { rev, active, prepared, fingerprint: preparedFingerprint(prepared, active) };
    }
    return best ?? { rev: 0, active: 1, prepared: {}, fingerprint: preparedFingerprint({}, 1) };
}

export interface SpellPrepRequest {
    casterId: string; action: SpellPrepAction; spellList: number;
    classId?: string; spellId?: string; classNumber?: number; className?: string;
    /** Re-composed against the held row after a stale answer, before any retry. */
    plan?: () => Promise<SpellListSwitchPlan>;
    refresh?: () => Promise<unknown>;
    committed?: (plan: SpellListSwitchPlan) => void;
    /** The caster row the caller rendered from (a fallback CAS source when no cache holds the caster). */
    row?: Row | null;
    /** The caller's gameplay gate, run against the EXACT truth each POST carries (the first send and the one retry after a
     * stale answer) — never against a display that may lag it (Codex S2 client review #1). */
    gate?: (truth: SpellPrepTruth) => Verdict;
}
export type SpellPrepOutcome = { ok: true; result: Row; truth: SpellPrepTruth } | { ok: false; message: string; status?: number; local?: boolean };

async function post(req: SpellPrepRequest, truth: SpellPrepTruth, switchPlan?: SpellListSwitchPlan): Promise<{ status: number; body: Row | null }> {
    const savedPlan = switchPlan ? (({ fortunateOffers: _offers, memberExpectedRevs: _revs, ...saved }) => saved)(switchPlan) : undefined;
    const body = {
        schema: SPELL_PREP_SCHEMA, operation_id: crypto.randomUUID().toUpperCase(), caster_id: upper(req.casterId), action: req.action,
        spell_list: req.spellList, class_id: req.classId ? upper(req.classId) : undefined, spell_id: req.spellId ? upper(req.spellId) : undefined,
        class_number: req.classNumber, expected_rev: truth.rev, expected_fingerprint: truth.fingerprint, switch_plan: savedPlan,
        ...(req.action === 'reset' ? { reset_log: { class_name: req.className ?? '' } } : {}),
    };
    const command = req.action === 'reset' ? withLogParticipants(body, [freshestCasterRow(req.casterId, req.row)]) : body;
    for (let readAttempt = 0; ; readAttempt++) {
        const res = await saveAppliedTruth(ENDPOINT, command, { method: 'POST', headers: { 'Content-Type': 'application/json' },
            body: JSON.stringify(command), credentials: 'same-origin' });
        try { return { status: res.status, body: await withAsyncDeadline(() => res.json() as Promise<Row>, 20000) }; }
        catch (error) {
            // A lost/truncated answer may already have committed. Reuse its operation receipt, never mint another write.
            if (res.ok && readAttempt === 0) continue;
            if (asRow(error)?.name === 'TimeoutError') throw error;
            return { status: res.status, body: null };
        }
    }
}

/** Send one prepared-spell write. Never throws; the message on a refusal is the server's text. */
export async function commitSpellPrep(req: SpellPrepRequest): Promise<SpellPrepOutcome> {
    let truth = currentSpellPrepTruth(req.casterId, req.row ?? null);
    for (let attempt = 0; attempt < 2; attempt++) {
        if (attempt > 0) truth = currentSpellPrepTruth(req.casterId, req.row ?? null);
        const verdict = req.gate ? req.gate(truth) : { ok: true as const };
        if (!verdict.ok) return { ok: false, message: verdict.message, local: attempt === 0, status: attempt === 0 ? undefined : 409 };
        let r: { status: number; body: Row | null };
        let plan: SpellListSwitchPlan | undefined;
        try { if (req.plan) plan = await withAsyncDeadline(() => req.plan!(), 20000); } catch (error) {
            const e = asRow(error);
            return { ok: false, message: e?.name === 'TimeoutError' ? 'spell-prep:plan-timeout'
                : String(e?.refusal ?? e?.reason ?? (error instanceof Error ? error.message : error)), local: true };
        }
        try { r = await post(req, truth, plan); } catch (error) {
            return { ok: false, message: asRow(error)?.name === 'TimeoutError' || String(asRow(error)?.message).includes('truth-save-timeout')
                ? 'spell-prep:save-timeout' : 'Network error — try again.' };
        }
        const b = r.body ?? {};
        if (r.status === 200 && b.ok === true) {
            noteSpellPrepTruth(req.casterId, { rev: int(b.rev), active: int(b.active, 1), prepared: b.prepared });
            // Publish only the exact saved sections/effects. The socket owner retains immutable bytes until delivery.
            if (asRow(b.frame)) {
                const saved = b.frame as TruthCommand;
                const caster = freshestCasterRow(req.casterId, req.row);
                const itemSnapshot = { item_type: ['item'], item_name: [req.className ?? ''] };
                const rows = req.action === 'reset' ? [{ roll_type_1: 'reset_prepared_spell_list', roll_result: req.spellList,
                    roll_item_id: classIdOfSlot(readPlayerCharacterField(caster, `class_${req.classNumber ?? 1}`)),
                    roll_by: upper(req.casterId), item_id_used_arr: itemSnapshot }]
                    : (asRow(saved.effects)?.rows as Row[] | undefined)?.map(row => row.roll_type_1 === 'prepare_spell_list'
                        ? { ...row, item_id_used_arr: itemSnapshot } : row) ?? [];
                const frame = withLogParticipants({ ...saved, rows }, [caster]);
                window.ftPushSavedSpellPrep?.(frame);
                w().ftApplySpellPrepFrame?.(frame);
            }
            try { if (plan) req.committed?.(plan); } catch { /* the save and frame are already confirmed */ }
            return { ok: true, result: b, truth: truthByCaster.get(upper(req.casterId))! };
        }
        if (r.status === 409 && b.error === 'stale' && attempt === 0) {
            try { if (req.refresh) await withAsyncDeadline(() => req.refresh!(), 20000); }
            catch (error) { return { ok: false, message: asRow(error)?.name === 'TimeoutError' ? 'spell-prep:actor-inputs-timeout'
                : 'This character changed elsewhere — reload the list and try again.', status: 409 }; }
            noteSpellPrepTruth(req.casterId, { rev: int(b.rev), active: int(b.active, 1), prepared: b.prepared });
            truth = truthByCaster.get(upper(req.casterId))!;
            // Show the adopted truth at once (the tab re-derives its flags + counts), whether or not the retry is allowed.
            window.dispatchEvent(new CustomEvent('ft:spell-prep-truth', { detail: { actor_id: upper(req.casterId), rev: truth.rev, adopted: true,
                sections: { prepared: truth.prepared, spell_list_prepared: truth.active } } }));
            continue;
        }
        return { ok: false, message: String(b.error ?? `The prepared spells could not be saved (${r.status}).`), status: r.status };
    }
    return { ok: false, message: 'This character changed elsewhere — reload the list and try again.', status: 409 };
}

/** The freshest row the page holds for the caster (highest movement_rev), else the fallback. */
export function freshestCasterRow(casterId: string, fallback?: Row | null): Row | null {
    let best: Row | null = fallback ?? null;
    for (const row of heldRows(casterId)) {
        if (!best || int(encounterCreatureField(row.encounter_creatures, 'movement_rev'), 0) > int(encounterCreatureField(best.encounter_creatures, 'movement_rev'), 0)) best = row;
    }
    return best;
}

/** Switch plans include fatigue and its gain hooks. Reset retains its existing save. */
export async function commitSpellPrepList(opts: { casterId: string; action: 'switch' | 'reset'; list: number; classNumber?: number; className?: string; row?: Row | null }): Promise<SpellPrepOutcome> {
    if (!Number.isInteger(opts.list) || opts.list < 1 || opts.list > 3) return { ok: false, message: 'Unknown spell list.', local: true };
    if (opts.action === 'reset') return commitSpellPrep({ casterId: opts.casterId, action: 'reset', spellList: opts.list, classNumber: opts.classNumber, className: opts.className, row: opts.row });
    return runRowOnly(opts.casterId, async (): Promise<SpellPrepOutcome> => {
        let held = await withAsyncDeadline(() => ensureActorRowHeld(opts.casterId,
            encounterCreatureField(opts.row?.encounter_creatures, 'encounter_id'), { allowRowOnly: true }), 20000);
        if (held.kind === 'refused') return { ok: false, message: held.message, local: true };
        return commitSpellPrep({ casterId: opts.casterId, action: 'switch', spellList: opts.list, classNumber: opts.classNumber, className: opts.className, row: held.row,
            committed: plan => {
                const notify = (window as unknown as { ftSpellListSwitchCommitted?: (eid: string, aid: string, plan: SpellListSwitchPlan) => void }).ftSpellListSwitchCommitted;
                if (held.kind !== 'refused') notify?.(held.encounterId, upper(opts.casterId), plan);
            },
            refresh: async () => {
                const loader = window as unknown as { ftResyncEncounter?: (id: string) => Promise<unknown>; fetchAndCacheCaster?: (id: string, opts: { skipEncounterSeed: boolean; throwOnError: boolean }) => Promise<unknown> };
                if (held.kind !== 'refused' && held.encounterId) await loader.ftResyncEncounter?.(held.encounterId);
                else await loader.fetchAndCacheCaster?.(opts.casterId, { skipEncounterSeed: true, throwOnError: true });
            },
            plan: async () => {
                // A stale save must refresh statuses as well as the prepared-map CAS before another stack is planned.
                held = await withAsyncDeadline(() => ensureActorRowHeld(opts.casterId,
                    held.kind === 'refused' ? undefined : held.encounterId, { allowRowOnly: true }), 20000);
                if (held.kind === 'refused') throw new Error(held.message);
                const row = freshestCasterRow(opts.casterId, held.row) ?? held.row;
                return w().ftPlanSpellListSwitch!(upper(opts.casterId), opts.list, opts.classNumber ?? 1, row, held.encounterId);
            },
        });
    }).catch((error: unknown) => ({ ok: false as const, local: true,
        message: asRow(error)?.name === 'TimeoutError' ? 'spell-prep:actor-inputs-timeout' : String(asRow(error)?.message ?? error) }));
}
